import { useFormikContext } from 'formik';
import { useRfiForm, useFormManageFields } from './RfiFormProvider';
import {
  AssignedTo,
  CustomIdentifier,
  Question,
  DueDate,
  LocationDetails,
  Location,
  ScrollErrorToView,
  SuggestedAnswer,
  Title,
  RfiType,
  Status,
  ExternalId,
  Watchers,
  CoReviewers,
} from './fields';
import { CustomAttributesFields } from './fields/CustomAttributes/CustomAttributesFields';
import {
  CostImpactInCreateForm as CostImpact,
  ScheduleImpactInCreateForm as ScheduleImpact,
  PriorityInCreateForm as Priority,
  DisciplineInCreateForm as Discipline,
  CategoryInCreateForm as Category,
} from '../components/Form';
import { ISSUE_ATTRIBUTE_NAMES } from '../constants/issue-attribute-names';
import IssuePreviewNoWorkflowConfiguration from '../components/IssuePreviewNoWorkflowConfiguration';
import { RfiStatus } from '../sdk';
import featureFlags from '../helpers/featureFlags';
import { FEATURE_FLAGS } from '../constants/featureFlags';

const RfiForm = () => {
  const formik = useFormikContext();
  const isStatusDraft = formik.values.status === RfiStatus.Draft;
  const displayRfiType = !isStatusDraft;
  const isDefaultCustomAttributesEnabled = !!featureFlags.get(
    FEATURE_FLAGS.DEFAULT_CUSTOM_ATTRIBUTES,
  );

  const {
    isRfiCustomIdentifierVisible,
    isDisplayCoReviewers,
    showNoWorkflowConfiguration,
    setSelectedTransition,
    prevSelectedTransition,
    refs,
  } = useRfiForm();

  // When the LIB_MANAGE_FIELDS FF is off, every selector returns the no-op
  // value (visible / non-mandatory) so the rendered tree is byte-identical
  // to the prior behavior.
  const { isVisible } = useFormManageFields();

  const noWorkflow = showNoWorkflowConfiguration && (
    <IssuePreviewNoWorkflowConfiguration
      closePopup={() => {
        formik.setFieldValue(ISSUE_ATTRIBUTE_NAMES.STATUS, prevSelectedTransition.serverStatus);
        setSelectedTransition(prevSelectedTransition);
      }}
    />
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      {noWorkflow}
      <ScrollErrorToView fieldRefs={refs} />
      {/* Always-render: Status, CustomIdentifier, RfiType, Title, AssignedTo
          are the five CONSTANT_MANDATORY_FIELDS — short-circuited to visible
          inside useManageFields, so we also do not gate them on isVisible()
          here (belt + suspenders). Every other field below is gated, including
          Question and DueDate: the API contract marks them
          `canToggleVisibility: false`, but we still respect whatever the BE
          sends so a malformed response cannot leak past the form. */}
      <Status />
      {isRfiCustomIdentifierVisible && <CustomIdentifier />}
      {displayRfiType && <RfiType />}
      <Title />
      {isVisible(ISSUE_ATTRIBUTE_NAMES.QUESTION) && <Question />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.SUGGESTED_ANSWER) && <SuggestedAnswer />}
      <AssignedTo />
      {isDisplayCoReviewers && isVisible(ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS) && <CoReviewers />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.DUE_DATE) && <DueDate />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.LOCATION_IDS) && <Location />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION) && <LocationDetails />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.COST) && <CostImpact />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.SCHEDULE) && <ScheduleImpact />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.PRIORITY) && <Priority />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.DISCIPLINE) && <Discipline />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.CATEGORY) && <Category />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.SUB_ID) && <ExternalId />}
      {isDefaultCustomAttributesEnabled && <CustomAttributesFields />}
      {isVisible(ISSUE_ATTRIBUTE_NAMES.WATCHERS) && <Watchers />}
    </form>
  );
};
export default RfiForm;
